import type { Vault, VaultHolder } from 'src/pages/admin/services/vaultService';
import type { ApiErrorInfo } from 'src/pages/admin/utils/decimal-input';

import { ENGINE_ERROR_KEYS } from '../params/helpers';
import {
  formatInstant,
  formatNav,
  formatSignedUsdc,
  formatUnits,
  formatUsdc,
  signOf,
} from '../vaults/helpers';
import { HOLDER_LOCKUP_STATUSES, INVESTORS_NS as NS, VAULT_NOT_FOUND } from './constants';

const EMPTY = '—';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Which vault the page shows (PRD §14.1). `/investors` with no id opens the first vault; an id the console does not
 * list falls back the same way ("a detail URL with an unknown ID falls back to its list page"). `redirect` is set
 * when the URL must change; `vaultId` is null while there is nothing to show.
 */
export const resolveVault = (
  vaults: Vault[],
  id: string | undefined
): { vaultId: string | null; redirect: string | null; unknown: boolean } => {
  if (id && vaults.some((v) => v.vaultId === id))
    return { vaultId: id, redirect: null, unknown: false };
  const first = vaults[0]?.vaultId ?? null;
  return { vaultId: null, redirect: first, unknown: !!id };
};

export interface HolderCells {
  units: string;
  value: string;
  hwm: string;
  avgCost: string;
  pnl: string;
  pnlSign: -1 | 0 | 1;
}

/**
 * One §14.5 row as text. Units keep their 6 dp; value and P&L are USDC at 2 dp and NAVs at 4 dp, all rounded down
 * (§6.6), a loss toward −∞. Before the vault's first cut-off value and P&L are "—", never a zero.
 */
export const holderCells = (h: VaultHolder): HolderCells => {
  const pnl = h.cumulativePnl === null ? EMPTY : formatSignedUsdc(h.cumulativePnl);
  return {
    units: formatUnits(h.shares),
    value: h.value === null ? EMPTY : formatUsdc(h.value),
    hwm: formatNav(h.highWaterMarkNav),
    avgCost: formatNav(h.avgCostNav),
    pnl,
    // The colour follows the figure shown: "0.004000" displays as 0.00, so it is neutral, not a green gain.
    pnlSign: h.cumulativePnl === null ? 0 : signOf(pnl),
  };
};

/**
 * The lock-up cell: the status label and, when a calendar lock is still ahead, its date. A manager with a locked
 * top-up shows both — the stake, and the top-up's date. A status added later shows raw, never a crash.
 */
export const lockupText = (
  h: VaultHolder,
  translate: Translate
): { label: string; detail: string | null } => {
  const known = (HOLDER_LOCKUP_STATUSES as readonly string[]).includes(h.lockupStatus);
  const label = known ? translate(`${NS}.lockup.${h.lockupStatus}`) : h.lockupStatus;
  if (!h.unlockAt) return { label, detail: null };
  const at = formatInstant(h.unlockAt);
  const detailKey =
    h.lockupStatus === 'STAKE_LOCKED' ? `${NS}.lockup.topUpUntil` : `${NS}.lockup.until`;
  return { label, detail: translate(detailKey, { at }) };
};

/** The load banner: one translated line; the raw upstream text is not shown. */
export const holdersErrorMessage = (info: ApiErrorInfo, translate: Translate): string => {
  if (info.code === VAULT_NOT_FOUND) return translate(`${NS}.notFound`);
  return translate((info.code && ENGINE_ERROR_KEYS[info.code]) || `${NS}.loadFailed`);
};

// `middle` moved to vaults/helpers (shared by Investors, Records and the vault address); re-exported for this page.
export { middle } from '../vaults/helpers';

/** The page's own URL for a vault: what the switcher navigates to. */
export const investorsPath = (root: string, vaultId: string): string =>
  `${root}/${encodeURIComponent(vaultId)}`;
